/* Mocap Atlas cart UX guard
 * Purpose:
 * - Shopify Checkout Add-to-cart limit remains the source of truth for max quantity = 1.
 * - Horizon's native product-form.js still performs the actual add-to-cart request and cart drawer refresh.
 * - This script only improves UX: fixed quantity display, already-in-cart button state,
 *   cross-license duplicate format blocking, and a submit preflight.
 */
(function () {
  'use strict';

  var CART_ROOT = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
  var CART_CACHE_MS = 1200;
  var cartCache = {
    time: 0,
    promise: null,
    data: null,
  };

  var PRODUCT_CACHE_MS = 5 * 60 * 1000;
  var LICENSE_SUFFIX_RE = /-(INDIE|PRO)$/i;
  var productCache = {
    time: 0,
    promise: null,
    data: null,
    variantsById: {},
  };

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function readCart(force) {
    var currentTime = now();

    if (!force && cartCache.data && currentTime - cartCache.time < CART_CACHE_MS) {
      return Promise.resolve(cartCache.data);
    }

    if (!force && cartCache.promise) return cartCache.promise;

    cartCache.promise = fetch(CART_ROOT + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Could not read the current cart.');
        return response.json();
      })
      .then(function (cart) {
        cartCache.data = cart;
        cartCache.time = now();
        return cart;
      })
      .finally(function () {
        cartCache.promise = null;
      });

    return cartCache.promise;
  }

  function invalidateCartCache() {
    cartCache.time = 0;
    cartCache.data = null;
    cartCache.promise = null;
  }

  function canonicalSku(sku) {
    return String(sku || '').trim().replace(LICENSE_SUFFIX_RE, '');
  }

  function currentProductJsonUrl() {
    var path = window.location && window.location.pathname ? window.location.pathname : '';
    if (path.indexOf('/products/') === -1) return '';
    return path.replace(/\/$/, '') + '.js';
  }

  function cacheProductVariants(product) {
    productCache.variantsById = {};

    if (!product || !Array.isArray(product.variants)) return product;

    product.variants.forEach(function (variant) {
      if (!variant || !variant.id) return;
      productCache.variantsById[String(variant.id)] = variant;
    });

    return product;
  }

  function readProductVariants(force) {
    var url = currentProductJsonUrl();
    if (!url) return Promise.resolve(null);

    var currentTime = now();

    if (!force && productCache.data && currentTime - productCache.time < PRODUCT_CACHE_MS) {
      return Promise.resolve(productCache.data);
    }

    if (!force && productCache.promise) return productCache.promise;

    productCache.promise = fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Could not read product variant data.');
        return response.json();
      })
      .then(function (product) {
        productCache.data = cacheProductVariants(product);
        productCache.time = now();
        return productCache.data;
      })
      .catch(function (error) {
        // Product JSON is only needed to compare Indie/Pro SKUs for the same file format.
        // If it is unavailable, keep the older same-variant guard as a safe fallback.
        productCache.data = null;
        productCache.variantsById = {};
        console.warn('Mocap Atlas cart UX guard: product variant lookup failed; falling back to variant-id duplicate checks.', error);
        return null;
      })
      .finally(function () {
        productCache.promise = null;
      });

    return productCache.promise;
  }

  function selectedVariantSku(form) {
    var variantId = selectedVariantId(form);
    var variant = variantId ? productCache.variantsById[String(variantId)] : null;
    return variant && variant.sku ? String(variant.sku).trim() : '';
  }

  function selectedCanonicalSku(form) {
    return canonicalSku(selectedVariantSku(form));
  }

  function cartContainsCanonicalSku(cart, canonical) {
    if (!cart || !Array.isArray(cart.items) || !canonical) return false;

    return cart.items.some(function (item) {
      var itemCanonical = canonicalSku(item && item.sku);
      return itemCanonical && itemCanonical === canonical;
    });
  }

  function cartContainsSelectedFormat(cart, form) {
    var canonical = selectedCanonicalSku(form);

    if (canonical && cartContainsCanonicalSku(cart, canonical)) {
      return true;
    }

    return cartContainsVariant(cart, selectedVariantId(form));
  }

  function cartContainsVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || !variantId) return false;

    return cart.items.some(function (item) {
      return String(item.id) === String(variantId) || String(item.variant_id) === String(variantId);
    });
  }

  function productForms(root) {
    return Array.from((root || document).querySelectorAll('form[action*="/cart/add"]')).filter(function (form) {
      return form.querySelector('[name="id"]');
    });
  }

  function findProductForm(target) {
    if (!target || !target.closest) return null;
    var form = target.closest('form[action*="/cart/add"]');
    if (form && form.querySelector('[name="id"]')) return form;
    return null;
  }

  function selectedVariantId(form) {
    var input = form && form.querySelector('[name="id"]');
    return input ? String(input.value || '').trim() : '';
  }

  function quantityInputs(root) {
    return Array.from(
      (root || document).querySelectorAll(
        'form[action*="/cart/add"] input[name="quantity"], input[name="updates[]"], input[name^="updates"]'
      )
    );
  }

  function forceQuantityOne(root) {
    quantityInputs(root).forEach(function (input) {
      input.value = '1';
      input.defaultValue = '1';
      input.min = '1';
      input.max = '1';
      input.step = '1';
      input.setAttribute('value', '1');
      input.setAttribute('min', '1');
      input.setAttribute('max', '1');
      input.setAttribute('step', '1');
      input.readOnly = true;
      input.setAttribute('aria-label', 'Quantity fixed to 1 for digital downloads');
    });
  }

  function injectStyle() {
    if (document.getElementById('mocapatlas-cart-ux-guard-style')) return;

    var style = document.createElement('style');
    style.id = 'mocapatlas-cart-ux-guard-style';
    style.textContent = [
      '.cart-items__quantity .quantity-selector .quantity-minus,',
      '.cart-items__quantity .quantity-selector .quantity-plus,',
      'form[action*="/cart/add"] .quantity-selector .quantity-minus,',
      'form[action*="/cart/add"] .quantity-selector .quantity-plus {',
      '  display: none !important;',
      '}',
      '.cart-items__quantity .quantity-selector,',
      'form[action*="/cart/add"] .quantity-selector {',
      '  --quantity-selector-width: 3.25rem;',
      '}',
      '.cart-items__quantity .quantity-selector input[type="number"],',
      'form[action*="/cart/add"] .quantity-selector input[type="number"] {',
      '  max-width: 3.25rem !important;',
      '  text-align: center;',
      '  pointer-events: none;',
      '}',
      '.shopify-payment-button,',
      'shopify-accelerated-checkout {',
      '  display: none !important;',
      '}',
      '.mocapatlas-already-in-cart {',
      '  opacity: 0.65;',
      '  cursor: not-allowed;',
      '}',
    ].join('\n');

    document.head.appendChild(style);
  }

  function addToCartButton(form) {
    return form
      ? form.querySelector('button[name="add"], button[type="submit"], input[type="submit"], [type="submit"]')
      : null;
  }

  function buttonTextNode(button) {
    if (!button) return null;
    return (
      button.querySelector('.add-to-cart-text__content span span') ||
      button.querySelector('.add-to-cart-text__content') ||
      button
    );
  }

  function setButtonText(button, text) {
    var target = buttonTextNode(button);
    if (target) target.textContent = text;
  }

  function rememberOriginalButtonText(button) {
    if (!button || button.dataset.mocapAtlasOriginalText) return;
    var target = buttonTextNode(button);
    button.dataset.mocapAtlasOriginalText = (target && target.textContent ? target.textContent : button.textContent || '').trim();
  }

  function markAlreadyInCart(form, isAlreadyInCart) {
    var button = addToCartButton(form);
    if (!button) return;

    rememberOriginalButtonText(button);

    if (isAlreadyInCart) {
      button.dataset.mocapAtlasDisabled = 'true';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('mocapatlas-already-in-cart');
      setButtonText(button, 'Already in cart');
      return;
    }

    if (button.dataset.mocapAtlasDisabled === 'true') {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.classList.remove('mocapatlas-already-in-cart');
      setButtonText(button, button.dataset.mocapAtlasOriginalText || 'Add to cart');
      delete button.dataset.mocapAtlasDisabled;
    }
  }

  function errorElement(form) {
    var component = form && form.closest('product-form-component');
    return component ? component.querySelector('[ref="addToCartTextError"]') : null;
  }

  function liveRegion(form) {
    var component = form && form.closest('product-form-component');
    return component ? component.querySelector('[ref="liveRegion"]') : null;
  }

  function setFormMessage(form, message) {
    var error = errorElement(form);
    var live = liveRegion(form);

    if (error) {
      error.classList.remove('hidden');
      error.setAttribute('aria-live', 'assertive');

      var textNode = null;
      for (var i = 0; i < error.childNodes.length; i += 1) {
        if (error.childNodes[i].nodeType === Node.TEXT_NODE) {
          textNode = error.childNodes[i];
          break;
        }
      }

      if (textNode) {
        textNode.textContent = message;
      } else {
        error.appendChild(document.createTextNode(message));
      }
    }

    if (live) live.textContent = message;
  }

  function clearFormMessage(form) {
    var error = errorElement(form);
    var live = liveRegion(form);

    if (error) {
      error.classList.add('hidden');
      error.removeAttribute('aria-live');
    }

    if (live) live.textContent = '';
  }

  function syncFormWithCart(form, cart) {
    if (!form) return;

    forceQuantityOne(form);

    var variantId = selectedVariantId(form);
    if (!variantId) return;

    var isAlreadyInCart = cartContainsSelectedFormat(cart, form);
    markAlreadyInCart(form, isAlreadyInCart);

    if (!isAlreadyInCart) clearFormMessage(form);
  }

  function syncAllForms(force) {
    var forms = productForms(document);
    if (!forms.length) {
      forceQuantityOne(document);
      return Promise.resolve();
    }

    forceQuantityOne(document);

    return Promise.all([readCart(force), readProductVariants(force)]).then(function (results) {
      var cart = results[0];
      forms.forEach(function (form) {
        syncFormWithCart(form, cart);
      });
    }).catch(function (error) {
      console.warn('Mocap Atlas cart UX guard: cart sync failed.', error);
    });
  }

  function submitThroughTheme(form) {
    form.dataset.mocapAtlasBypass = 'true';

    window.setTimeout(function () {
      delete form.dataset.mocapAtlasBypass;
    }, 2000);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      var button = addToCartButton(form);
      if (button) button.click();
    }
  }

  function onSubmit(event) {
    var form = findProductForm(event.target);
    if (!form) return;

    if (form.dataset.mocapAtlasBypass === 'true') return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    forceQuantityOne(form);

    var variantId = selectedVariantId(form);
    if (!variantId) {
      submitThroughTheme(form);
      return;
    }

    Promise.all([readCart(true), readProductVariants(true)])
      .then(function (results) {
        var cart = results[0];

        if (cartContainsSelectedFormat(cart, form)) {
          markAlreadyInCart(form, true);
          setFormMessage(form, 'This download format is already in your cart. Remove it before choosing a different license tier.');
          return;
        }

        markAlreadyInCart(form, false);
        clearFormMessage(form);
        submitThroughTheme(form);
      })
      .catch(function (error) {
        console.warn('Mocap Atlas cart UX guard: preflight failed, falling back to native Shopify limit.', error);
        submitThroughTheme(form);
      });
  }

  function onQuantityInput(event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches('input[name="quantity"], input[name="updates[]"], input[name^="updates"]')) {
      return;
    }

    if (String(target.value) !== '1') target.value = '1';
  }

  function attach() {
    injectStyle();
    forceQuantityOne(document);
    syncAllForms(false);
  }

  if (!document.documentElement.dataset.mocapAtlasCartUxGuardAttached) {
    document.documentElement.dataset.mocapAtlasCartUxGuardAttached = 'true';

    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('input', onQuantityInput, true);
    document.addEventListener('change', onQuantityInput, true);

    document.addEventListener('variant:selected', function () {
      window.setTimeout(function () {
        forceQuantityOne(document);
      }, 50);
    });

    document.addEventListener('variant:update', function () {
      window.setTimeout(function () {
        syncAllForms(true);
      }, 80);
    });

    document.addEventListener('cart:update', function () {
      invalidateCartCache();
      window.setTimeout(function () {
        syncAllForms(true);
      }, 80);
    });

    var observerTimer = 0;
    var observer = new MutationObserver(function () {
      window.clearTimeout(observerTimer);
      observerTimer = window.setTimeout(attach, 120);
    });

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();